import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SvgLink from '../components/SvgLink';
import { aiToolsData } from '../data/ai-tools-data';

const AIToolsContainer = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
`;

const ContentWrapper = styled.div`
    max-width: 1000px;
    margin: 0 auto;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 50px;
`;

const Title = styled.h1`
    font-size: 3rem;
    margin-bottom: 15px;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const Description = styled.p`
    font-size: 1rem;
    opacity: 0.8;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
`;

const ToolsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
    margin-top: 50px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const ToolCard = styled.a`
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    text-decoration: none;
    color: white;
    display: block;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(155, 89, 182, 0.1) 0%, rgba(142, 68, 173, 0.1) 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
        background: rgba(255, 255, 255, 0.15);

        &::before {
            opacity: 1;
        }

        .tool-link-icon {
            transform: translateX(5px);
        }
    }

    @media (max-width: 768px) {
        padding: 20px;
    }
`;

const ToolHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    position: relative;
    z-index: 1;
`;

const ToolTitle = styled.h2`
    font-size: 1.8rem;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;

    @media (max-width: 768px) {
        font-size: 1.4rem;
    }
`;

const ToolLinkIcon = styled.div`
    transition: transform 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    opacity: 0.7;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const ToolDescription = styled.p`
    font-size: 1.1rem;
    line-height: 1.8;
    opacity: 0.9;
    margin: 0;
    position: relative;
    z-index: 1;
`;

const ToolIcon = styled.div`
    font-size: 3rem;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
`;

const PriceBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    margin-top: 15px;
    background: ${props => props.isFree ? 'rgba(39, 174, 96, 0.2)' : 'rgba(52, 152, 219, 0.2)'};
    border: 1px solid ${props => props.isFree ? 'rgba(39, 174, 96, 0.4)' : 'rgba(52, 152, 219, 0.4)'};
    color: ${props => props.isFree ? '#27ae60' : '#3498db'};
    position: relative;
    z-index: 1;
`;

const PriceText = styled.span`
    font-size: 1rem;
    font-weight: 700;
`;

const PriceDetails = styled.div`
    margin-top: 10px;
    font-size: 0.85rem;
    opacity: 0.8;
    line-height: 1.6;
    position: relative;
    z-index: 1;
`;

const FilterContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    margin-bottom: 40px;
    position: relative;
    z-index: 1;
`;

const FilterButton = styled.button`
    padding: 10px 20px;
    border-radius: 25px;
    border: 2px solid ${props => props.active ? 'rgba(155, 89, 182, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
    background: ${props => props.active ? 'rgba(155, 89, 182, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
    color: white;
    font-size: 1rem;
    font-weight: ${props => props.active ? '600' : '400'};
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
        background: ${props => props.active ? 'rgba(155, 89, 182, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
        border-color: ${props => props.active ? 'rgba(155, 89, 182, 1)' : 'rgba(255, 255, 255, 0.5)'};
        transform: translateY(-2px);
    }

    @media (max-width: 768px) {
        padding: 8px 16px;
        font-size: 0.9rem;
    }
`;

const CategoryBadge = styled.div`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 15px;
    font-size: 0.85rem;
    font-weight: 600;
    margin-top: 10px;
    background: rgba(155, 89, 182, 0.2);
    border: 1px solid rgba(155, 89, 182, 0.4);
    color: rgba(155, 89, 182, 1);
    position: relative;
    z-index: 1;
`;

const YearInfo = styled.div`
    display: flex;
    gap: 15px;
    margin-top: 10px;
    font-size: 0.85rem;
    opacity: 0.8;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
`;

const YearItem = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const YearLabel = styled.span`
    opacity: 0.7;
`;

const YearValue = styled.span`
    font-weight: 600;
`;

const AIToolsPage = () => {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = [
        { id: 'all', label: t('aiTools.filterAll') },
        { id: 'image', label: t('aiTools.filterImage') },
        { id: 'video', label: t('aiTools.filterVideo') },
        { id: 'music', label: t('aiTools.filterMusic') },
        { id: 'voice', label: t('aiTools.filterVoice') },
        { id: 'development', label: t('aiTools.filterDevelopment') },
        { id: 'other', label: t('aiTools.filterOther') }
    ];

    // Transform data with translations
    const tools = aiToolsData.map(tool => ({
        ...tool,
        description: t(tool.descriptionKey),
        price: t(tool.priceKey),
        priceDetails: t(tool.priceDetailsKey)
    }));

    const filteredTools = selectedCategory === 'all' 
        ? tools 
        : tools.filter(tool => tool.category === selectedCategory);

    return (
        <AIToolsContainer>
            <ContentWrapper>
                <Header>
                    <Title>{t('aiTools.title')}</Title>
                    <Subtitle>{t('aiTools.subtitle')}</Subtitle>
                    <Description>
                        {t('aiTools.description')}
                    </Description>
                </Header>

                <FilterContainer>
                    {categories.map((category) => (
                        <FilterButton
                            key={category.id}
                            active={selectedCategory === category.id}
                            onClick={() => setSelectedCategory(category.id)}
                        >
                            {category.label}
                        </FilterButton>
                    ))}
                </FilterContainer>

                <ToolsGrid>
                    {filteredTools.map((tool) => (
                        <ToolCard
                            key={tool.id}
                            href={tool.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ToolIcon>{tool.icon}</ToolIcon>
                            <ToolHeader>
                                <ToolTitle>
                                    {tool.name}
                                    <ToolLinkIcon className="tool-link-icon">
                                        <SvgLink />
                                    </ToolLinkIcon>
                                </ToolTitle>
                            </ToolHeader>
                            <ToolDescription>{tool.description}</ToolDescription>
                            <CategoryBadge>
                                {t(`aiTools.category.${tool.category}`)}
                            </CategoryBadge>
                            {tool.firstReleaseYear && tool.lastVersionYear && (
                                <YearInfo>
                                    <YearItem>
                                        <YearLabel>{t('aiTools.firstRelease')}:</YearLabel>
                                        <YearValue>{tool.firstReleaseYear}</YearValue>
                                    </YearItem>
                                    <YearItem>
                                        <YearLabel>{t('aiTools.lastVersion')}:</YearLabel>
                                        <YearValue>{tool.lastVersionYear}</YearValue>
                                    </YearItem>
                                </YearInfo>
                            )}
                            <PriceBadge isFree={tool.isFree}>
                                <PriceText>{tool.price}</PriceText>
                            </PriceBadge>
                            {tool.priceDetails && (
                                <PriceDetails>{tool.priceDetails}</PriceDetails>
                            )}
                        </ToolCard>
                    ))}
                </ToolsGrid>
            </ContentWrapper>
        </AIToolsContainer>
    );
};

export default AIToolsPage;

